import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const HealthPackagesManagement = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPackage, setEditingPackage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    price: '',
    description: '',
    color_code: '#0760ad',
    is_featured: false,
    features: [{ description: '', order: 0 }],
    bulkFeatures: ''
  });

  const API_URL = import.meta.env.VITE_API_URL;

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API_URL}/packages/`);
      setPackages(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load packages');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFeatureChange = (index, e) => {
    const newFeatures = [...formData.features];
    newFeatures[index][e.target.name] = e.target.value;
    setFormData({
      ...formData,
      features: newFeatures
    });
  };

  const handleBulkFeaturesImport = () => {
    if (!formData.bulkFeatures.trim()) return;
    
    const featuresArray = formData.bulkFeatures
      .split('\n')
      .filter(line => line.trim() !== '')
      .map((line, index) => ({
        description: line.trim(),
        order: formData.features.length + index
      }));
    
    setFormData({
      ...formData,
      features: [...formData.features, ...featuresArray],
      bulkFeatures: ''
    });
  };

  const addFeatureField = () => {
    setFormData({
      ...formData,
      features: [...formData.features, { description: '', order: formData.features.length }]
    });
  };

  const removeFeatureField = (index) => {
    const newFeatures = [...formData.features];
    newFeatures.splice(index, 1);
    setFormData({
      ...formData,
      features: newFeatures
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const packageData = {
        title: formData.title,
        price: formData.price,
        description: formData.description,
        color_code: formData.color_code,
        is_featured: formData.is_featured,
        features: formData.features
          .filter(f => f.description.trim() !== '')
          .map((f, index) => ({
            description: f.description,
            order: f.order || index
          }))
      };
  
      if (editingPackage) {
        await axios.put(`${API_URL}/packages/${editingPackage.id}/`, packageData);
        toast.success('Package updated successfully');
      } else {
        await axios.post(`${API_URL}/packages/`, packageData);
        toast.success('Package created successfully');
      }
      
      fetchPackages();
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error('Failed to save package');
      if (error.response) {
        console.error('Response data:', error.response.data);
      }
    }
  };

  const editPackage = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      title: pkg.title,
      price: pkg.price,
      description: pkg.description || '',
      color_code: pkg.color_code,
      is_featured: pkg.is_featured,
      features: pkg.features.length > 0 
        ? pkg.features.map(f => ({ description: f.description, order: f.order }))
        : [{ description: '', order: 0 }],
      bulkFeatures: ''
    });
    setIsModalOpen(true);
  };

  const deletePackage = async (id) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      try {
        await axios.delete(`${API_URL}/packages/${id}/`);
        toast.success('Package deleted successfully');
        fetchPackages();
      } catch (error) {
        console.error('Error deleting package:', error);
        toast.error('Failed to delete package');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      price: '',
      description: '',
      color_code: '#0760ad',
      is_featured: false,
      features: [{ description: '', order: 0 }],
      bulkFeatures: ''
    });
    setEditingPackage(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Health Packages Management</h1>
        <button
          onClick={openCreateModal}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Add New Package
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Features</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Featured</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {packages.map((pkg) => (
              <motion.tr 
                key={pkg.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div 
                      className="h-4 w-4 rounded-full mr-2" 
                      style={{ backgroundColor: pkg.color_code }}
                    ></div>
                    <div className="font-medium text-gray-900">{pkg.title}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-500">{pkg.price}</td>
                <td className="px-6 py-4 text-gray-500">
                  <div className="max-h-20 overflow-y-auto">
                    {pkg.features.length} features
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                    ${pkg.is_featured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {pkg.is_featured ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => editPackage(pkg)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deletePackage(pkg.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  {editingPackage ? 'Edit Package' : 'Add New Package'}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  ></textarea>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Color Code</label>
                    <div className="flex items-center">
                      <input
                        type="color"
                        name="color_code"
                        value={formData.color_code}
                        onChange={handleInputChange}
                        className="w-10 h-10 mr-2 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        name="color_code"
                        value={formData.color_code}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_featured"
                      checked={formData.is_featured}
                      onChange={handleInputChange}
                      id="is_featured"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_featured" className="ml-2 block text-sm text-gray-700">
                      Featured Package
                    </label>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">Features</label>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={addFeatureField}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + Add Single
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bulk Add Features (one per line)
                    </label>
                    <div className="flex space-x-2">
                      <textarea
                        name="bulkFeatures"
                        value={formData.bulkFeatures}
                        onChange={handleInputChange}
                        rows="3"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter features, one per line"
                      />
                      <button
                        type="button"
                        onClick={handleBulkFeaturesImport}
                        className="self-end px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                      >
                        Import
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 max-h-60 overflow-y-auto p-2 border rounded">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <input
                          type="text"
                          name="description"
                          value={feature.description}
                          onChange={(e) => handleFeatureChange(index, e)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Feature description"
                        />
                        <input
                          type="number"
                          name="order"
                          value={feature.order}
                          onChange={(e) => handleFeatureChange(index, e)}
                          className="w-16 ml-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Order"
                        />
                        <button
                          type="button"
                          onClick={() => removeFeatureField(index)}
                          className="ml-2 text-red-500 hover:text-red-700 p-1"
                          title="Remove feature"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="mt-2 text-sm text-gray-500">
                    {formData.features.filter(f => f.description.trim() !== '').length} features added
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {editingPackage ? 'Update Package' : 'Create Package'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default HealthPackagesManagement;